"use client"

import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { resolveCoverageState, TIER_TO_SERVICE } from "@/lib/quiz/coverage"
import {
  findInsurer,
  findPackage,
  type InsurersData,
} from "@/lib/quiz/insurers"
import type { BaseTierId } from "@/lib/quiz/types"

interface Props {
  baseTier: BaseTierId
  insurers: InsurersData
  insurerId?: string
  insurerPackageId?: string
  onRequestProForma: () => void
}

const CARD_CLASS =
  "mx-auto max-w-[556px] rounded-[16px] border border-white bg-white/80 p-6 transition-all duration-[250ms] hover:bg-white hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)] md:p-8"

export function CoverageBand({
  baseTier,
  insurers,
  insurerId,
  insurerPackageId,
  onRequestProForma,
}: Props) {
  const t = useTranslations("quiz.result")
  const localeRaw = useLocale()
  const locale: "de" | "en" | "fr" =
    localeRaw === "en" || localeRaw === "fr" ? localeRaw : "de"

  // No insurance picked — skip the band entirely. The "request a pro-forma"
  // affordance lives next to the main CTA instead so the user doesn't see an
  // orphan band asking about an insurer they never named.
  if (!insurerId || insurerId === "none" || !insurerPackageId) {
    return (
      <div className={CARD_CLASS}>
        <p className="type-title-md text-neutral-900">{t("coverageUnknown")}</p>
        <ProFormaLink
          onClick={onRequestProForma}
          label={t("requestProForma")}
        />
      </div>
    )
  }

  const serviceId = TIER_TO_SERVICE[baseTier]
  const result = resolveCoverageState(
    insurers,
    insurerId,
    insurerPackageId,
    serviceId,
    locale
  )
  // intentional display fallbacks — keeps the band readable when data drift causes a missing display name
  const insurer = findInsurer(insurers, insurerId)?.name ?? insurerId
  const pkg =
    findPackage(insurers, insurerId, insurerPackageId)?.name ?? insurerPackageId

  let headline: string
  switch (result.state) {
    case "known":
      headline = t("coverageKnown", {
        amount: String(result.amount ?? 0), // next-intl placeholder coercion, same pattern as progress-indicator.tsx
        insurer,
        package: pkg,
      })
      break
    case "likely":
      headline = t("coverageLikely")
      break
    case "not_covered":
      headline = t("coverageNotCovered")
      break
    case "unknown":
    default:
      headline = t("coverageUnknown")
  }

  return (
    <div className={CARD_CLASS}>
      <p className="type-title-md text-neutral-900">{headline}</p>
      {result.notes && (
        <p className="type-body-sm mt-2 text-neutral-500">{result.notes}</p>
      )}
      <ProFormaLink onClick={onRequestProForma} label={t("requestProForma")} />
    </div>
  )
}

function ProFormaLink({
  onClick,
  label,
}: {
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="type-body-lg text-deep-blue-700 hover:text-deep-blue-900 mt-4 inline-flex cursor-pointer items-center gap-1 underline underline-offset-4"
    >
      {label} →
    </button>
  )
}
